/**
 * SSE Sync Client
 * Server-Sent Events client for real-time sync updates.
 *
 * Ref-counted connection lifecycle
 * --------------------------------
 * Multiple independent React consumers (`ImportProgressIndicator`,
 * `SyncNotifications`, sync admin pages) share this singleton. Each one
 * calls `connect()` on mount and `disconnect()` on unmount. Without
 * ref-counting, the first unmount tears down the EventSource that every
 * other consumer still depends on.
 *
 * Semantics:
 *  - `connect()` increments `consumerCount`. The EventSource is only
 *    actually opened on the 0 -> 1 transition. Subsequent `connect()`
 *    calls while already connected are no-ops (besides the count bump).
 *  - `disconnect()` decrements `consumerCount`. The EventSource is only
 *    actually closed on the 1 -> 0 transition. Otherwise the connection
 *    stays open for the remaining consumers.
 *  - `forceDisconnect()` resets the count to 0 and closes immediately
 *    (used by logout / hard auth-state changes).
 *
 * React StrictMode safety: a double-mount sequence (mount -> unmount ->
 * mount -> unmount) takes the count 0 -> 1 -> 0 -> 1 -> 0 only if no other
 * consumer is alive. While other consumers hold a refcount, the actual
 * EventSource is preserved across StrictMode double-mounts because the
 * count never reaches zero.
 */

import { streamUrl } from '@/platform/api/stream-ticket'
import type { SseSyncEvent } from '@/platform/sync/types'
import { logger } from '@/shared/utils/logger'

type SyncEventHandler = (event: SseSyncEvent) => void
type ConnectionStateHandler = (state: ConnectionState) => void

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

interface SyncClientOptions {
  url?: string
  reconnectInterval?: number
  maxReconnectAttempts?: number
  withCredentials?: boolean
}

interface SyncClientConfig {
  onSyncStarted?: SyncEventHandler
  onSyncProgress?: SyncEventHandler
  onSyncCompleted?: SyncEventHandler
  onSyncFailed?: SyncEventHandler
  onSyncPaused?: SyncEventHandler
  onSyncCancelled?: SyncEventHandler
  onConnectionStateChange?: ConnectionStateHandler
}

class SyncClient {
  private eventSource: EventSource | null = null
  private reconnectTimer: NodeJS.Timeout | null = null
  private reconnectAttempts = 0
  private isIntentionallyDisconnected = false

  /**
   * Number of live consumers that have called `connect()` without a
   * matching `disconnect()`. The underlying EventSource is opened on
   * the 0 -> 1 transition and closed on the 1 -> 0 transition.
   */
  private consumerCount = 0

  private readonly url: string
  /** Bumped by every open and every disconnect: a ticket request that resolves after it is stale. */
  private openAttempt = 0
  private readonly reconnectInterval: number
  private readonly maxReconnectAttempts: number
  private readonly withCredentials: boolean

  private handlers: Map<string, Set<SyncEventHandler>> = new Map()
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set()

  constructor(options: SyncClientOptions = {}) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string URL means unset and must fall back to the default endpoint
    this.url = options.url || '/api/imports/sync/stream'
    this.reconnectInterval = options.reconnectInterval ?? 5000
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10
    this.withCredentials = options.withCredentials !== false
  }

  /**
   * Acquire a reference to the SSE connection.
   *
   * If this is the first consumer (0 -> 1 transition) the EventSource
   * is opened. Otherwise the call is a no-op besides incrementing the
   * shared refcount.
   *
   * The `config` argument is honored on every call: handlers passed in
   * are registered onto the shared event bus. They MUST be unsubscribed
   * by the caller (use `on()` directly if you want a tear-down handle).
   */
  connect(config?: SyncClientConfig): void {
    this.consumerCount += 1

    // Register handlers if provided (regardless of whether we actually
    // open a new connection - additional consumers can subscribe to the
    // already-open stream).
    if (config) {
      if (config.onSyncStarted) this.on('sync_started', config.onSyncStarted)
      if (config.onSyncProgress) this.on('sync_progress', config.onSyncProgress)
      if (config.onSyncCompleted) this.on('sync_completed', config.onSyncCompleted)
      if (config.onSyncFailed) this.on('sync_failed', config.onSyncFailed)
      if (config.onSyncPaused) this.on('sync_paused', config.onSyncPaused)
      if (config.onSyncCancelled) this.on('sync_cancelled', config.onSyncCancelled)
      if (config.onConnectionStateChange) {
        this.onConnectionStateChange(config.onConnectionStateChange)
      }
    }

    // Only the first consumer actually opens the EventSource. Every
    // subsequent caller is just bumping the refcount.
    if (this.consumerCount > 1) {
      return
    }

    // 0 -> 1 transition: open the connection.
    this.openConnection()
  }

  /**
   * Open the underlying EventSource. Internal: callers should use
   * `connect()` so the refcount stays in sync.
   */
  private openConnection(): void {
    if (this.eventSource?.readyState === EventSource.OPEN) {
      return // Already connected
    }

    this.isIntentionallyDisconnected = false
    this.notifyConnectionStateChange('connecting')

    // The stream authenticates with a single-use TICKET (`platform/api/stream-ticket.ts`) — never the JWT in
    // the URL. A ticket is spent by the first connection, so every (re)connect asks for a new one; a
    // disconnect (or a newer open) while the ticket is in flight makes this attempt stale.
    const attempt = ++this.openAttempt
    void this.openWithTicket(attempt)
  }

  private async openWithTicket(attempt: number): Promise<void> {
    try {
      const urlWithAuth = await streamUrl(this.url, 'sync-stream')
      if (attempt !== this.openAttempt || this.isIntentionallyDisconnected) return
      if (!urlWithAuth) throw new Error('No stream ticket')

      this.eventSource = new EventSource(urlWithAuth, {
        withCredentials: this.withCredentials,
      })

      // Connection opened. The backend emits a named `connected` event
      // on stream open; we don't need the event payload here, so the
      // listener takes no argument.
      this.eventSource.addEventListener('connected', () => {
        this.reconnectAttempts = 0
        this.notifyConnectionStateChange('connected')
      })

      // Error handler. The native EventSource fires `error` for both
      // transient drops and permanent failures - we always schedule a
      // reconnect unless the caller asked us to stay down.
      const source = this.eventSource
      source.onerror = () => {
        // Close at once: left open, the native EventSource retries by itself
        // with the SAME url — a ticket already spent — and earns a 401 per try.
        // The server also ends every stream on purpose (bounded lifetime), so
        // this is the normal rotation path, not only a failure path.
        source.close()
        if (this.eventSource === source) this.eventSource = null
        this.notifyConnectionStateChange('error')

        if (!this.isIntentionallyDisconnected) {
          this.scheduleReconnect()
        }
      }

      // Note on keep-alive: the backend writes SSE comment frames
      // (`: ping\n\n`) for keep-alive. SSE comment lines are dropped by
      // the EventSource parser and never surface as `onmessage` events,
      // so no client-side handler is needed.

      // Register event handlers
      this.registerEventHandlers()
    } catch (error) {
      this.notifyConnectionStateChange('error')
      logger.error('Failed to create EventSource:', error)
      this.scheduleReconnect()
    }
  }

  /**
   * Register all event handlers
   */
  private registerEventHandlers(): void {
    if (!this.eventSource) return

    const eventTypes = [
      'sync_started',
      'sync_progress',
      'sync_completed',
      'sync_failed',
      'sync_paused',
      'sync_cancelled',
      // Round 2 — FIX C: backend emits `role_changed` (and closes the
      // stream) when the user's role changes mid-flight. Registering it
      // here means the event surfaces via the same dispatch pipeline as
      // every other sync event; consumers wanting to react can subscribe
      // via `on('role_changed', ...)`. The shape doesn't match
      // SseSyncEvent but the JSON parser is tolerant — handlers that opt
      // in are responsible for narrowing.
      'role_changed',
    ]

    for (const eventType of eventTypes) {
      this.eventSource.addEventListener(eventType, (e) => {
        try {
          const data = JSON.parse(e.data) as SseSyncEvent
          this.emit(eventType, data)
        } catch (error) {
          logger.error(`Failed to parse SSE event (${eventType}):`, error)
        }
      })
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return // Already scheduled
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.notifyConnectionStateChange('disconnected')
      return
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, ... max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)

    this.reconnectAttempts++
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null

      // Don't auto-reconnect if every consumer has released the
      // connection in the meantime (e.g. user navigated away).
      if (this.consumerCount <= 0) {
        return
      }

      // Clean up old connection
      if (this.eventSource) {
        this.eventSource.close()
      }

      // Attempt reconnection - `openConnection()` (not `connect()`)
      // because the refcount is already tracking the live consumers.
      this.openConnection()
    }, delay)
  }

  /**
   * Release a reference to the SSE connection.
   *
   * Decrements the consumer refcount. The EventSource is only actually
   * torn down on the 1 -> 0 transition. While other consumers still
   * hold references, the stream stays open.
   *
   * Calling `disconnect()` without a prior `connect()` is a no-op (the
   * count is clamped at 0 to keep things resilient against double-free
   * patterns under React StrictMode).
   */
  disconnect(): void {
    if (this.consumerCount === 0) {
      return
    }

    this.consumerCount -= 1

    if (this.consumerCount > 0) {
      // Other consumers still hold the connection.
      return
    }

    // 1 -> 0 transition: tear down for real.
    this.closeConnection()
  }

  /**
   * Reopen NOW if consumers still want the stream but it is down — after the
   * tab comes back to the foreground or the network returns. A stream that
   * gave up after `maxReconnectAttempts` would otherwise stay dead for the
   * rest of the session.
   */
  wake(): void {
    if (this.consumerCount <= 0) return
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) return
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempts = 0
    this.openConnection()
  }

  /**
   * Force-close the connection regardless of refcount. Use on logout
   * or any other hard auth-state reset where the underlying token
   * becomes invalid.
   */
  forceDisconnect(): void {
    this.consumerCount = 0
    this.closeConnection()
  }

  /**
   * Internal: close the underlying EventSource and cancel any pending
   * reconnect timer. Does not touch the consumer refcount.
   */
  private closeConnection(): void {
    this.isIntentionallyDisconnected = true
    this.openAttempt += 1 // a ticket request still in flight is now stale

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    this.notifyConnectionStateChange('disconnected')
  }

  /**
   * Register event handler
   */
  on(eventType: string, handler: SyncEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set())
    }
    this.handlers.get(eventType)!.add(handler)

    // Return unsubscribe function
    return () => {
      this.off(eventType, handler)
    }
  }

  /**
   * Unregister event handler
   */
  off(eventType: string, handler: SyncEventHandler): void {
    const handlers = this.handlers.get(eventType)
    if (handlers) {
      handlers.delete(handler)
      if (handlers.size === 0) {
        this.handlers.delete(eventType)
      }
    }
  }

  /**
   * Emit event to all registered handlers
   */
  private emit(eventType: string, data: SseSyncEvent): void {
    const handlers = this.handlers.get(eventType)
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data)
        } catch (error) {
          logger.error(`Error in ${eventType} handler:`, error)
        }
      })
    }
  }

  /**
   * Register connection state change handler
   */
  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler)
    return () => {
      this.connectionStateHandlers.delete(handler)
    }
  }

  /**
   * Notify all connection state handlers
   */
  private notifyConnectionStateChange(state: ConnectionState): void {
    this.connectionStateHandlers.forEach((handler) => {
      try {
        handler(state)
      } catch (error) {
        logger.error('Error in connection state handler:', error)
      }
    })
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    if (!this.eventSource) return 'disconnected'
    if (this.eventSource.readyState === EventSource.CONNECTING) return 'connecting'
    if (this.eventSource.readyState === EventSource.OPEN) return 'connected'
    return 'disconnected'
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.getConnectionState() === 'connected'
  }
}

// Export singleton instance
export const syncClient = new SyncClient()
